import { Workflow, Layers, ShieldCheck, TrendingUp } from "lucide-react";
import { Container, Section, Heading, Lede, Eyebrow, LinkButton, FeatureCard } from "../components/ui";
import { Seo } from "../lib/head";
import { FaqSection, CtaBand, faqPageJsonLd, type Faq } from "../components/marketing";

const FAQS: Faq[] = [
  {
    q: "What does WeConsult Rx do for a consultant pharmacist?",
    a: "It runs the entire monthly engagement: chart ingest from PointClickCare/EHR, a rules-and-AI clinical review aligned to ASCP/Beers/STOPP, prescriber and DON e-signature, and QuickBooks Online invoicing — for SNF medication regimen reviews plus ASC and dialysis compliance audits.",
  },
  {
    q: "Does it work for a solo pharmacist and for a firm?",
    a: "Both. Solo pharmacists cut time per review and get paid faster; firms get one standardized, defensible workflow across every pharmacist and facility, so throughput scales without adding audit risk.",
  },
  {
    q: "Which settings does it cover?",
    a: "Skilled nursing (SNF) medication regimen reviews, ambulatory surgical centers (AAAHC pharmacy, DEA, vaccine temp), and dialysis (CMS CfC 42 CFR 494, AAMI water) — on one platform.",
  },
  {
    q: "Is resident data secure?",
    a: "WeConsult Rx is built for a HIPAA-conscious workflow with a BAA available, and demos use synthetic data only — never real PHI.",
  },
];

export function ConsultantPharmacistSoftware() {
  return (
    <>
      <Seo
        title="Consultant Pharmacist Software for MRR & Compliance"
        description="Consultant pharmacist software that runs the monthly review end to end — chart ingest, ASCP/Beers/STOPP rules and AI flags, prescriber and DON e-sign, QBO invoicing — for SNF, ASC, and dialysis."
        path="/consultant-pharmacist-software"
        jsonLd={faqPageJsonLd(FAQS)}
      />

      <Section className="pb-8">
        <Container>
          <div className="max-w-3xl">
            <Eyebrow>Consultant pharmacist software</Eyebrow>
            <Heading level={1} className="mt-3">
              Consultant pharmacist software for SNF medication reviews — and ASC and dialysis.
            </Heading>
            <Lede className="mt-6">
              One platform for the whole monthly engagement: ingest the charts, run the clinical rules,
              e-sign the recommendations, and invoice — without stitching together three tools.
            </Lede>
            <div className="mt-8 flex flex-wrap gap-3">
              <LinkButton to="/demo">Book a demo</LinkButton>
              <LinkButton to="/features" variant="secondary">
                See all features
              </LinkButton>
            </div>
          </div>
        </Container>
      </Section>

      <Section tone="soft">
        <Container>
          <Heading level={2}>Built for how consultant pharmacists actually work</Heading>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            <FeatureCard icon={<Workflow className="h-6 w-6" aria-hidden />} title="The whole loop, one platform">
              Chart ingest → rules + AI flags → prescriber/DON e-sign → QBO invoice. No exporting, no separate signing tool, no month-end invoicing scramble.
            </FeatureCard>
            <FeatureCard icon={<Layers className="h-6 w-6" aria-hidden />} title="SNF, ASC and dialysis">
              Medication regimen reviews plus AAAHC/DEA/vaccine-temp and CMS CfC §494 audits — the same platform across your whole book.
            </FeatureCard>
            <FeatureCard icon={<ShieldCheck className="h-6 w-6" aria-hidden />} title="Defensible by design">
              Every recommendation is aligned to ASCP/Beers/STOPP and CMS F-tag guidance and captured as a dated, e-signed record.
            </FeatureCard>
            <FeatureCard icon={<TrendingUp className="h-6 w-6" aria-hidden />} title="Scales with your caseload">
              Solo pharmacists move faster; firms standardize output across every pharmacist so growth doesn't mean a new hire for every contract.
            </FeatureCard>
          </div>
        </Container>
      </Section>

      <FaqSection items={FAQS} />

      <CtaBand
        title="One platform for the whole month"
        body="See a live monthly review from chart ingest to invoice, or start with a free first-facility review."
        primary={{ to: "/demo", label: "Book a demo" }}
        secondary={{ to: "/pricing", label: "See pricing" }}
      />
    </>
  );
}
